import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { BLOCK_ID, BLOCK_NAME, COMMUNITY_ID, DELETED, DESCRIPTION, LIST, NEW, ZONE_ID } from "./consts";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { addSchema, AddBlockFormValues } from "./validations";
import { toast } from "@/components/Toast";
import { useAddPropertyBlockMutation, useDeletePropertyBlockMutation, useGetActivePropertyZones, useGetCommunities, useGetPropertyBlockByCommunityZone } from "@/services";
import { getBaseRoute, getRequiredFields, ROLE_BOM } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";

const useAddEdit = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const basePath = getBaseRoute(useLocation().pathname);
  const {action, id, zoneId} = useParams();
  const isEdit = action == routes.edit;
  const [keys, setKeys] = useState({
    community: Math.random() * 3,
    zone: Math.random() * 3
  })

  const form = useForm<AddBlockFormValues>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      [COMMUNITY_ID]: "",
      [ZONE_ID]: "",
      [LIST]: [
        {
          [BLOCK_ID]: "",
          [BLOCK_NAME]: "",
          [DESCRIPTION]: "",
          [NEW]: true,
          [DELETED]: false
        }
      ]      
    },
  });

  const requiredFields = getRequiredFields(addSchema);

  const masterDataPermission = useSelector((state: RootState) => state.currentData.masterDataPermission);
  const currentRole = useSelector((state: RootState) => state.currentData.currentRole);
  const isCamBom = location.pathname.startsWith("/cam") || location.pathname.startsWith("/bom") || (location.pathname.startsWith("/owner") && currentRole == ROLE_BOM);
  const hasDeletePermission = useMemo(() => isCamBom ? masterDataPermission?.delete : true, [masterDataPermission]);
  
  const breadcrumbData = [
    {
      link: `/${basePath}/${routes.masterData}/${routes.propertyBlock}`,
      name: `Property Block`,
    },
    {
      link: "",
      name: isEdit ? "Edit Block" : "Add Block",
    },
  ];

  const onBackClick = () => {
    navigate(`/${basePath}/${routes.masterData}/${routes.propertyBlock}`);
  };

  const { data: communitiesData, isLoading: isLoadingCommunities } =
    useGetCommunities({});

  const communityId = useWatch({control: form.control, name: COMMUNITY_ID});

  const { data: zoneList, isLoading: isLoadingZone } =
    useGetActivePropertyZones({
      communityId: (isEdit ? id : communityId) as string,
      queryParams: {
        enabled: isEdit ? !!id : !!communityId
      }
    });
    
    const { data: blockData, isLoading: isLoadingBlock } =
    useGetPropertyBlockByCommunityZone({
      communityId: id as string,
      zoneId: zoneId as string,
      queryParams: {
        enabled: !!id
      }
    });

  useEffect(() => {
    if (blockData?.data) {
      form.reset({
        [COMMUNITY_ID]: id?.toString() || "",
        [ZONE_ID]: zoneId?.toString() || "",
        [LIST]: blockData?.data?.map((data: any) => ({
          ...data,
          [BLOCK_ID]: data?.[BLOCK_ID]?.toString() || "", 
          [NEW]: false,
          [DELETED]: false
        })),
      })
      setKeys({
        community: Math.random() * 2,
        zone: Math.random() * 2
      });
    }
  }, [blockData]);
  
  const { mutateAsync: addInfo, isPending: isAddPending } =
    useAddPropertyBlockMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      // onSuccess: () => {
      //   toast.success("Property Block Added Successfully", {
      //     className: "!bg-green-600 !text-white",
      //   });
      //   queryClient.invalidateQueries({
      //     queryKey: ["property-blocks"],
      //   });
      //   onBackClick();
      // },
    });

    const { mutateAsync: deleteInfo, isPending: isDeletePending } =
    useDeletePropertyBlockMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      // onSuccess: () => {
      //   toast.success("Property Block Deleted Successfully", {
      //     className: "!bg-green-600 !text-white",
      //   });
      //   queryClient.invalidateQueries({
      //     queryKey: ["property-blocks-zone-wise"],
      //   });
      //   setOpenDeleteDialog(false);
      // },
    });

    
  const onSubmit = async(data: AddBlockFormValues) => {
    const addData = data?.[LIST]?.filter((data: any) => data[NEW] && !data[DELETED]);
    const deleteData = data?.[LIST]?.filter((data: any) => data[DELETED]);

    const additionalPromises: Promise<any>[] = [];
    if (addData?.length) {
      additionalPromises.push(addInfo({ data: {
        communityId: data[COMMUNITY_ID],
        [LIST]: addData?.map((val: any) => ({[BLOCK_NAME]: val?.[BLOCK_NAME], [ZONE_ID]: data?.[ZONE_ID], [DESCRIPTION]: val?.[DESCRIPTION], active: true}))
      }}))
    }
    deleteData?.forEach((val: any) => val?.blockId ? additionalPromises.push(deleteInfo({ id: val?.blockId, communityId })) : "")
    
    await Promise.all(additionalPromises);

    if (addData?.length || deleteData?.length) {
      toast.success(`Property Block ${isEdit ? "Updated" : "Added"} Successfully`);
      queryClient.invalidateQueries({
        queryKey: ["property-blocks-zone-wise"],
      });
    }
    onBackClick();
  };

  const list = useWatch({control: form.control, name: LIST})
  const onAddMore = () => {
    form.setValue(LIST, [...list || [], {[BLOCK_NAME]: "", [DESCRIPTION]: "", [NEW]: true, [DELETED]: false}])
  }

  const onRemove = (index: number) => {
    const newList = [...list];
    const deleted = newList.findIndex((_: any, idx: number) => idx == index)
    newList[deleted] = {...newList[deleted], [DELETED]: true}
    form.setValue(LIST, [...newList])
  }
  
  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    onCancel: onBackClick,
    communitiesData: communitiesData?.data || [],
    isLoadingCommunities,
    onAddMore,
    onRemove,
    isDeletePending,
    list,
    keys,
    isLoadingBlock,
    isLoadingZone,
    isEdit,
    zoneList: zoneList?.data,
    communityId,
    hasDeletePermission
  };
};

export default useAddEdit;
